import React, { useEffect, useState } from "react";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

import MemberInfo from "./MemberInfo";
import BookShelf from "./BookShelf";
import CartSummary from "./CartSummary";
import AnalyticsPanel from "./AnalyticsPanel";
import OrderPanel from "./OrderPanel";

function formatCharges(charges) {
    if (!charges) return 0;
    if (typeof charges === "number") return charges;
    if (typeof charges === "string") {
        const num = parseFloat(charges);
        return isNaN(num) ? charges : num;
    }
    if (Array.isArray(charges)) {
        return charges.reduce((acc, item) => acc + Number(item.amt || 0), 0);
    }
    if (typeof charges === "object") {
        return charges.amt || 0;
    }
    return 0;
}

export default function MemberCheckout({
    member,
    selectedMember,
    onBack = () => { },
    shelfItems = [],
    cartItems = [],
    onReturnBook = () => { },
    onRemoveFromCart = () => { },
    onCheckout = () => { },
    onAddDamage = () => { },
    walkInType = "WALK_IN",
    onWalkInTypeChange = () => { },
    onAddScannedBook = () => { },
    checkoutLoading = false,
}) {
    /* ✅ HOOKS ALWAYS ON TOP */
    const [shippingMethod, setShippingMethod] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("");
    const [deliverySlot, setDeliverySlot] = useState("");
    const [showFullAnalytics, setShowFullAnalytics] = useState(false);

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    useEffect(() => {
        if (walkInType === "WALK_IN" || walkInType === "VIRTUAL") {
            setShippingMethod("Pickup");
            setDeliverySlot("");
        } else if (walkInType === "DELIVERY") {
            setShippingMethod("Delivery");
        }
    }, [walkInType, setShippingMethod]);

    /* ---------- SAFE DATA ---------- */
    const safeShelfItems = Array.isArray(shelfItems) ? shelfItems : [];
    const safeCartItems = Array.isArray(cartItems) ? cartItems : [];
    const damage_charges = formatCharges(member?.damageCharges);

    /* ---------- CREDIT CALCULATION ---------- */
    const issueCredits = safeCartItems
        .filter((item) => item.status === "Issuing")
        .reduce((sum, item) => sum + Number(item.credits || 0), 0);

    const returnCredits = safeCartItems
        .filter((item) => item.status === "Returning")
        .reduce((sum, item) => sum + Number(item.credits || 0), 0);

    const memberBaseCredits = Number(member?.credits || 0);

    const finalAvailableCredits =
        memberBaseCredits - issueCredits + returnCredits;

    /* ---------- TOTAL CALCULATION ---------- */
    const shippingFee = 0;
    const totalAmount =
        Number(damage_charges || 0) + Number(shippingFee || 0);

    /* ---------- CHECKOUT VALIDATION ---------- */
    const handleLocalCheckout = (couponPayload = {}) => {
        if (finalAvailableCredits < 0) {
            alert(
                `Error: Cannot issue books. Member is short by ${Math.abs(
                    finalAvailableCredits
                )} credits.`
            );
            return;
        }

        const checkoutTotal = couponPayload.discountedTotal !== undefined ? couponPayload.discountedTotal : totalAmount;

        if (checkoutTotal > 0 && !shippingMethod) {
            alert("Please select a shipping method.");
            return;
        }

        if (walkInType === "DELIVERY" && !deliverySlot) {
            alert("Please select a delivery slot.");
            return;
        }

        if (!paymentMethod) {
            alert("Please select a payment method.");
            return;
        }

        onCheckout({
            finalAvailableCredits,
            shippingMethod,
            paymentMethod,
            totalAmount: checkoutTotal,
            orderType: walkInType,
            deliverySlot,
            coupon: couponPayload.coupon || null,
            giftCard: couponPayload.giftCard || null,
            giftCardCode: couponPayload.giftCardCode || "",
            customerNote: couponPayload.customerNote || ""
        });
    };

    const handleBack = () => {
        if (showFullAnalytics) {
            setShowFullAnalytics(false);
        } else {
            onBack();
        }
    };

    if (!member) return null;

    return (
        <div className="relative h-full w-full flex flex-col lg:flex-row gap-6 p-4 md:p-8 overflow-y-auto bg-gray-50">
            {/* Back */}
            <button
                onClick={handleBack}
                className="absolute top-4 left-4 bg-black text-white p-2 rounded-md shadow hover:bg-gray-800 z-10"
            >
                <ArrowLeftIcon className="h-5 w-5" />
            </button>

            {/* LEFT SIDE */}
            {!showFullAnalytics && (
                <div className="w-full lg:w-1/2 space-y-6 pt-12 lg:pt-0">
                    {/* MEMBER INFO */}
                    <MemberInfo member={member} />

                    {/* BOOK SHELF */}
                    <BookShelf
                        books={safeShelfItems}
                        onReturnBook={walkInType === "WALK_IN" ? null : onReturnBook}
                    />

                    {/* CART SUMMARY */}
                    <CartSummary
                        items={safeCartItems}
                        onRemoveItem={onRemoveFromCart}
                        onAddDamage={onAddDamage}
                    />
                </div>
            )}

            {/* RIGHT SIDE */}
            <div className={`w-full ${showFullAnalytics ? "pt-12" : "lg:w-1/2 pt-12 lg:pt-0"} space-y-6`}>
                <AnalyticsPanel
                    selectedMember={selectedMember || member}
                    isExpandedView={showFullAnalytics}
                    onToggleExpand={() => setShowFullAnalytics(!showFullAnalytics)}
                />

                {!showFullAnalytics && (
                    <OrderPanel
                        memberBaseCredits={memberBaseCredits}
                        issueCredits={issueCredits}
                        returnCredits={returnCredits}
                        finalAvailableCredits={finalAvailableCredits}
                        damage_charges={damage_charges}
                        totalAmount={totalAmount}
                        onShippingChange={setShippingMethod}
                        onPaymentChange={setPaymentMethod}
                        onCheckout={handleLocalCheckout}
                        orderType={walkInType}
                        onOrderTypeChange={onWalkInTypeChange}
                        deliverySlot={deliverySlot}
                        onDeliverySlotChange={setDeliverySlot}
                        token={token}
                        pincode={member?.pin_code}
                        shelfItems={safeShelfItems}
                        onAddScannedBook={onAddScannedBook}
                        checkoutLoading={checkoutLoading}
                    />
                )}
            </div>
        </div>
    );
}
